import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { LogoProps } from '@/types/Logo'
import logoSvgUrl from '@/assets/logo.svg?url'

/**
 * NCESP Logo Component
 *
 * A professional logo component that displays the NCESP branding
 * with support for different variants, sizes, and interaction patterns.
 * Follows accessibility guidelines and constitutional design requirements.
 */
const Logo: React.FC<LogoProps> = ({
  size = 40,
  variant = 'auto',
  sx = {},
  onClick,
  interactive = !!onClick,
  className,
  ariaLabel = 'National Council for Equal Shared Parenting Logo',
  showText = false,
  textVariant = 'abbreviated',
}) => {
  const theme = useTheme()

  // Determine colors based on variant
  const getColors = () => {
    switch (variant) {
      case 'light':
        return {
          primary: theme.palette.common.white,
          secondary: theme.palette.grey[300],
          accent: theme.palette.secondary.main,
        }
      case 'dark':
        return {
          primary: theme.palette.primary.main,
          secondary: theme.palette.primary.light,
          accent: theme.palette.secondary.main,
        }
      case 'auto':
      default:
        return {
          primary: theme.palette.primary.main,
          secondary: theme.palette.primary.light,
          accent: theme.palette.secondary.main,
        }
    }
  }

  const colors = getColors()

  // Logo SVG component imported from external file
  const logoSvg = (
    <img
      src={logoSvgUrl}
      width={size}
      height={size}
      alt={ariaLabel}
      style={{
        display: 'block',
        maxWidth: 400,
        width: '100%',
        height: 'auto',
      }}
    />
  )

  const logoText =
    textVariant === 'full'
      ? 'National Council for Equal Shared Parenting'
      : 'NCESP'

  const handleClick = interactive && onClick ? onClick : undefined

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      onClick={handleClick}
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: showText ? 1.5 : 0,
        cursor: interactive ? 'pointer' : 'default',
        border: 'none',
        background: 'none',
        padding: 0,
        transition: 'all 0.2s ease-in-out',
        '&:hover': interactive
          ? {
              transform: 'scale(1.05)',
              opacity: 0.8,
            }
          : {},
        '&:focus': interactive
          ? {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            }
          : {},
        ...sx,
      }}
      aria-label={interactive ? `${ariaLabel} - Click to navigate` : ariaLabel}
      role={interactive ? 'button' : 'img'}
      tabIndex={interactive ? 0 : undefined}
    >
      {logoSvg}

      {showText && (
        <Typography
          variant={textVariant === 'full' ? 'h6' : 'subtitle1'}
          component="span"
          sx={{
            fontWeight: 700,
            color: colors.primary,
            whiteSpace: 'nowrap',
            fontSize: textVariant === 'full' ? size * 0.3 : size * 0.4,
          }}
        >
          {logoText}
        </Typography>
      )}
    </Box>
  )
}

export default Logo
